import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  details?: string[];
  itemName?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  type = 'danger',
  details = [],
  itemName
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          alertBg: 'bg-red-50',
          alertBorder: 'border-red-200',
          alertText: 'text-red-800',
          alertSubtext: 'text-red-700',
          buttonBg: 'bg-red-600',
          buttonHover: 'hover:bg-red-700'
        };
      case 'warning':
        return {
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          alertBg: 'bg-yellow-50',
          alertBorder: 'border-yellow-200',
          alertText: 'text-yellow-800',
          alertSubtext: 'text-yellow-700',
          buttonBg: 'bg-yellow-600',
          buttonHover: 'hover:bg-yellow-700'
        };
      case 'info':
        return {
          iconBg: 'bg-forest-100',
          iconColor: 'text-forest-700',
          alertBg: 'bg-forest-50',
          alertBorder: 'border-forest-200',
          alertText: 'text-forest-800',
          alertSubtext: 'text-forest-700',
          buttonBg: 'bg-forest-600',
          buttonHover: 'hover:bg-forest-700'
        };
      default:
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          alertBg: 'bg-red-50',
          alertBorder: 'border-red-200',
          alertText: 'text-red-800',
          alertSubtext: 'text-red-700',
          buttonBg: 'bg-red-600',
          buttonHover: 'hover:bg-red-700'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`flex-shrink-0 w-10 h-10 ${styles.iconBg} rounded-full flex items-center justify-center mr-3`}>
                <AlertTriangle className={`h-5 w-5 ${styles.iconColor}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              {message}
              {itemName && (
                <span className="block mt-2">
                  <strong>"{itemName}"</strong>
                </span>
              )}
            </p>
            
            {details.length > 0 && (
              <div className={`${styles.alertBg} border ${styles.alertBorder} rounded-lg p-4`}>
                <div className="flex items-start">
                  <AlertTriangle className={`h-5 w-5 ${styles.iconColor} mt-0.5 mr-3 flex-shrink-0`} />
                  <div>
                    <h4 className={`text-sm font-medium ${styles.alertText} mb-2`}>
                      Cette action supprimera définitivement :
                    </h4>
                    <ul className={`text-sm ${styles.alertSubtext} space-y-1`}>
                      {details.map((detail, index) => (
                        <li key={index}>• {detail}</li>
                      ))}
                    </ul>
                    <p className={`text-sm font-medium ${styles.alertText} mt-2`}>
                      Cette action est irréversible.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2 ${styles.buttonBg} ${styles.buttonHover} text-white rounded-lg font-medium transition-colors`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
